import React, { useState, useEffect, useRef } from 'react';

const ShelfPolygonEditor = ({ imageUrl, shelfName, polygon, onSave, onCancel }) => {
    const canvasRef = useRef(null);
    const [points, setPoints] = useState(polygon || []);
    const [image, setImage] = useState(null);
    const [draggingPointIndex, setDraggingPointIndex] = useState(null);
    const hasDragged = useRef(false);

    useEffect(() => {
        if (!imageUrl) return;
        const img = new Image();
        img.src = imageUrl;
        img.onload = () => {
            setImage(img);
            draw(img, points);
        };
    }, [imageUrl]);

    useEffect(() => {
        if (image) {
            draw(image, points);
        }
    }, [points, image]);

    const draw = (img, currentPoints) => {
        const canvas = canvasRef.current;
        if (!canvas || !img) return;
        const ctx = canvas.getContext('2d');
        
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Draw image
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Draw polygon
        if (currentPoints && currentPoints.length > 0) {
            ctx.beginPath();
            ctx.moveTo(currentPoints[0].x * canvas.width, currentPoints[0].y * canvas.height);
            for (let i = 1; i < currentPoints.length; i++) {
                ctx.lineTo(currentPoints[i].x * canvas.width, currentPoints[i].y * canvas.height);
            }
            if (currentPoints.length > 2) {
                ctx.closePath();
            }
            ctx.strokeStyle = 'red';
            ctx.lineWidth = 2;
            ctx.stroke();
            ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
            ctx.fill();

            // Draw label
            if (shelfName) {
                const text = shelfName;
                ctx.font = 'bold 14px Arial';
                const textMetrics = ctx.measureText(text);
                const textWidth = textMetrics.width;
                const textHeight = 14;
                const labelX = currentPoints[0].x * canvas.width;
                const labelY = currentPoints[0].y * canvas.height - 10;

                ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
                ctx.fillRect(labelX - 2, labelY - textHeight, textWidth + 4, textHeight + 4);

                ctx.fillStyle = 'black';
                ctx.fillText(text, labelX, labelY);
            }
            
            // Draw points
            currentPoints.forEach((p, index) => {
                ctx.beginPath();
                ctx.arc(p.x * canvas.width, p.y * canvas.height, 6, 0, 2 * Math.PI);
                ctx.fillStyle = draggingPointIndex === index ? 'orange' : 'blue';
                ctx.fill();
                ctx.strokeStyle = 'white';
                ctx.lineWidth = 1;
                ctx.stroke();
            });
        }
    };

    const getCanvasCoordinates = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) / canvas.width;
        const y = (e.clientY - rect.top) / canvas.height;
        return { x, y };
    };

    const handleMouseDown = (e) => {
        const { x, y } = getCanvasCoordinates(e);
        const hitRadius = 10; // Pixels
        const canvas = canvasRef.current;
        
        const pointIndex = points.findIndex(p => {
            const px = p.x * canvas.width;
            const py = p.y * canvas.height;
            const dx = px - (x * canvas.width);
            const dy = py - (y * canvas.height);
            return Math.sqrt(dx * dx + dy * dy) < hitRadius;
        });

        if (pointIndex !== -1) {
            setDraggingPointIndex(pointIndex);
            hasDragged.current = false;
            e.stopPropagation();
        }
    };

    const handleMouseMove = (e) => {
        if (draggingPointIndex === null) return;
        
        hasDragged.current = true;
        const { x, y } = getCanvasCoordinates(e);
        const newPoints = [...points];
        newPoints[draggingPointIndex] = { x, y };
        setPoints(newPoints);
    };

    const handleMouseUp = () => {
        setDraggingPointIndex(null);
        // We delay resetting hasDragged to allow onClick to see it
        setTimeout(() => {
            hasDragged.current = false;
        }, 100);
    };

    const handleCanvasClick = (e) => {
        if (draggingPointIndex !== null || hasDragged.current) return;
        const { x, y } = getCanvasCoordinates(e);
        setPoints([...points, { x, y }]);
    };

    const clearPoints = () => setPoints([]);

    return (
        <div>
            <div className="mb-2">
                <button className="btn btn-secondary btn-sm me-2" onClick={clearPoints}>Καθαρισμός</button>
                <button className="btn btn-primary btn-sm me-2" onClick={() => onSave(points)}>Αποθήκευση</button>
                <button className="btn btn-light btn-sm" onClick={onCancel}>Ακύρωση</button>
            </div>
            <div style={{ position: 'relative', overflow: 'hidden', border: '1px solid #ccc' }}>
                <canvas 
                    ref={canvasRef} 
                    width={1200} 
                    height={600} 
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onClick={handleCanvasClick}
                    style={{ cursor: draggingPointIndex !== null ? 'grabbing' : 'crosshair', display: 'block' }}
                />
            </div>
            <small className="text-muted">Κάντε κλικ στο χάρτη για να ορίσετε τα σημεία του πολυγώνου. Μπορείτε να σύρετε τα υπάρχοντα σημεία για να τα μετακινήσετε.</small>
        </div>
    );
};

export default ShelfPolygonEditor;
